import { auditLogApi } from "@/services/auditLogApi";
import {
  AUDIT_ENTITY_ARTEFACT,
  AUDIT_ENTITY_PROJECT_DOCUMENT,
  type AuditAction,
  type AuditStage,
} from "@/constants/audit";
import type { Artifact } from "@/services/projectsApi";

/**
 * Audit entries for Evidence Base changes. Plain functions taking `projectId` rather than
 * a context-bound hook, because the upload form and the delete flow also run outside a
 * project page; callers skip recording when there is no project.
 */

const EVIDENCE_STAGE: AuditStage = "Evidence Gathering";

type DocumentAuditAction = Extract<
  AuditAction,
  "upload_document" | "add_document" | "remove_document" | "delete_document"
>;
type ArtefactAuditAction = Extract<
  AuditAction,
  "retry_artefact" | "add_artefact" | "remove_artefact" | "delete_artefact"
>;

/** Actions that take something away, so their snapshot is the `before` side. */
const REMOVING_ACTIONS: ReadonlySet<AuditAction> = new Set([
  "remove_document",
  "delete_document",
  "remove_artefact",
  "delete_artefact",
]);

export interface AuditedDocument {
  id: string;
  title?: string | null;
  filename?: string | null;
}

type AuditedArtefact = Pick<Artifact, "uid" | "name" | "type" | "sourceFilename">;

/**
 * `entity_name` is a VARCHAR(255) column, so a longer name would fail the insert and lose
 * the entry. The full value still goes in the before/after snapshot, which is unbounded.
 */
const ENTITY_NAME_MAX_LENGTH = 255;

const toEntityName = (value: string | null | undefined): string | null =>
  value ? value.slice(0, ENTITY_NAME_MAX_LENGTH) : null;

/** Removals carry the snapshot as `before`, additions as `after` — as section sources do. */
const snapshotSides = (action: AuditAction, snapshot: Record<string, unknown>) => {
  const json = JSON.stringify(snapshot);
  return REMOVING_ACTIONS.has(action) ? { before: json, after: null } : { before: null, after: json };
};

/** Resolves ids to the documents the caller has loaded, keeping the bare id when one is missing. */
export function findDocuments(ids: string[], documents: AuditedDocument[]): AuditedDocument[] {
  const byId = new Map(documents.map((d) => [d.id, d]));
  return ids.map((id) => byId.get(id) ?? { id });
}

/** Resolves uids to the artefacts the caller has loaded; unknown uids are dropped. */
export function findArtefacts<T extends AuditedArtefact>(uids: string[], artefacts: T[]): T[] {
  const wanted = new Set(uids);
  return artefacts.filter((a) => wanted.has(a.uid));
}

/** One entry per document. */
export function recordDocumentEvents(
  projectId: string,
  action: DocumentAuditAction,
  documents: AuditedDocument[]
): void {
  for (const doc of documents) {
    void auditLogApi.recordAuditEvent({
      projectId,
      entityType: AUDIT_ENTITY_PROJECT_DOCUMENT,
      entityId: doc.id,
      entityName: toEntityName(doc.title || doc.filename),
      action,
      stage: EVIDENCE_STAGE,
      ...snapshotSides(action, {
        title: doc.title || null,
        filename: doc.filename || null,
      }),
    });
  }
}

/** One entry per artefact. */
export function recordArtefactEvents(
  projectId: string,
  action: ArtefactAuditAction,
  artefacts: AuditedArtefact[]
): void {
  for (const artefact of artefacts) {
    void auditLogApi.recordAuditEvent({
      projectId,
      entityType: AUDIT_ENTITY_ARTEFACT,
      entityId: artefact.uid,
      entityName: toEntityName(artefact.name),
      action,
      stage: EVIDENCE_STAGE,
      ...snapshotSides(action, {
        name: artefact.name,
        type: artefact.type,
        file: artefact.sourceFilename,
      }),
    });
  }
}

/** One entry per workbook upload, named after the spreadsheet and listing what it extracted. */
export function recordArtefactUpload(
  projectId: string,
  sourceFilename: string,
  artefacts: AuditedArtefact[]
): void {
  void auditLogApi.recordAuditEvent({
    projectId,
    entityType: AUDIT_ENTITY_ARTEFACT,
    entityId: sourceFilename,
    entityName: toEntityName(sourceFilename),
    action: "upload_artefact",
    stage: EVIDENCE_STAGE,
    after: JSON.stringify({
      file: sourceFilename,
      artefacts: artefacts.map((a) => a.name).join(", "),
    }),
  });
}
